import type { AwpValue, ReferenceBandClause } from './types';

/** A range of numbers; each end may be open or closed, and either may be infinite. */
export interface Interval {
  low: number;
  lowInclusive: boolean;
  high: number;
  highInclusive: boolean;
}

type BoundOperator = 'LT' | 'LTE' | 'GT' | 'GTE';

function boundInterval(op: BoundOperator, bound: number): Interval {
  switch (op) {
    case 'LT':
      return { low: -Infinity, lowInclusive: false, high: bound, highInclusive: false };
    case 'LTE':
      return { low: -Infinity, lowInclusive: false, high: bound, highInclusive: true };
    case 'GT':
      return { low: bound, lowInclusive: false, high: Infinity, highInclusive: false };
    case 'GTE':
      return { low: bound, lowInclusive: true, high: Infinity, highInclusive: false };
  }
}

/** The values a result allows: one point for a number, a half-line for a bound such as "<0.01". */
export function valueInterval(value: AwpValue): Interval | null {
  switch (value.__typename) {
    case 'NumericBiomarkerValue':
      return { low: value.number, lowInclusive: true, high: value.number, highInclusive: true };
    case 'ComparatorBiomarkerValue':
      return boundInterval(value.operator, value.number);
    default:
      return null;
  }
}

/** A reference-band clause as an interval; BETWEEN is inclusive at both ends. */
export function clauseInterval(clause: ReferenceBandClause): Interval | null {
  if (clause.op === 'BETWEEN') {
    if (clause.low == null || clause.high == null) return null;
    return { low: clause.low, lowInclusive: true, high: clause.high, highInclusive: true };
  }
  return clause.value == null ? null : boundInterval(clause.op, clause.value);
}

/** Whether every value in `inner` is also in `outer`. */
export function contains(outer: Interval, inner: Interval): boolean {
  const lowOk = outer.low < inner.low || (outer.low === inner.low && (outer.lowInclusive || !inner.lowInclusive));
  const highOk =
    outer.high > inner.high || (outer.high === inner.high && (outer.highInclusive || !inner.highInclusive));
  return lowOk && highOk;
}

/** Whether the two intervals share at least one value. */
export function overlaps(a: Interval, b: Interval): boolean {
  const aEndsFirst = a.high < b.low || (a.high === b.low && !(a.highInclusive && b.lowInclusive));
  const bEndsFirst = b.high < a.low || (b.high === a.low && !(b.highInclusive && a.lowInclusive));
  return !aEndsFirst && !bEndsFirst;
}
